import { useState, useEffect, useCallback } from 'react';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { SubscriptionTier, TIER_CONFIGS } from '../components/SubscriptionModal';

const LOCAL_KEY = 'ts_subscription';

interface SubscriptionRecord {
  tier: SubscriptionTier;
  subscriptionId: string;
  activatedAt: number;
  expiresAt?: number; // ms timestamp — set for promo codes, absent for paid subs
}

function readLocal(): SubscriptionRecord | null {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    if (!raw) return null;
    const record: SubscriptionRecord = JSON.parse(raw);
    // Auto-expire promo codes
    if (record.expiresAt && Date.now() > record.expiresAt) {
      localStorage.removeItem(LOCAL_KEY);
      return null;
    }
    return record;
  } catch { return null; }
}

function writeLocal(record: SubscriptionRecord) {
  localStorage.setItem(LOCAL_KEY, JSON.stringify(record));
}

export function useTierConfig(tier: SubscriptionTier) {
  return TIER_CONFIGS.find(t => t.id === tier) ?? TIER_CONFIGS[0];
}

export function useSubscription(userId: string | null) {
  const [tier, setTierState]  = useState<SubscriptionTier>(() => readLocal()?.tier ?? 'free');
  const [subId, setSubId]     = useState<string | null>(() => readLocal()?.subscriptionId ?? null);
  const [loading, setLoading] = useState(false);

  // On login, pull subscription from Firestore to sync across devices
  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    getDoc(doc(db, 'users', userId))
      .then(snap => {
        const data = snap.data();
        const sub = data?.subscription;
        // Accept whitelisted tier OR PayPal-verified tier
        if (sub?.tier && sub.tier !== 'free') {
          // Don't apply if promo has expired
          if (sub.expiresAt && Date.now() > sub.expiresAt) return;
          const record: SubscriptionRecord = {
            tier:           sub.tier,
            subscriptionId: sub.subscriptionId ?? '',
            activatedAt:    sub.activatedAt ?? Date.now(),
            expiresAt:      sub.expiresAt,
          };
          writeLocal(record);
          setTierState(record.tier);
          setSubId(record.subscriptionId);
        }
      })
      .catch(console.warn)
      .finally(() => {
        // Also check Stripe subscription status
        if (userId) {
          fetch(`/scanner-api/stripe/status?userId=${userId}`)
            .then(r => r.json())
            .then(data => {
              if (data.subscribed && data.tier !== 'free') {
                const record: SubscriptionRecord = {
                  tier: data.tier as SubscriptionTier,
                  subscriptionId: data.subscriptionId || 'stripe',
                  activatedAt: Date.now(),
                };
                writeLocal(record);
                setTierState(record.tier);
                setSubId(record.subscriptionId);
              }
            })
            .catch(() => {});
        }
        setLoading(false);
      });
  }, [userId]);

  // Called after PayPal approval + backend verification
  const activate = useCallback(
    async (newTier: SubscriptionTier, subscriptionId: string) => {
      const record: SubscriptionRecord = {
        tier: newTier,
        subscriptionId,
        activatedAt: Date.now(),
      };
      writeLocal(record);
      setTierState(newTier);
      setSubId(subscriptionId);

      // Write to Firestore if logged in
      if (userId) {
        try {
          await setDoc(
            doc(db, 'users', userId),
            { subscription: record },
            { merge: true }
          );
        } catch (e) {
          console.warn('[subscription] Firestore write failed:', e);
        }
      }
    },
    [userId]
  );

  // Promotional period: open every gated feature for free.
  // Flip VITE_PROMOTIONAL_FREE=false in .env to restore tier gating.
  const promoFree = import.meta.env.VITE_PROMOTIONAL_FREE !== 'false';

  const canUseAI        = promoFree || tier === 'tactical' || tier === 'ghost';
  const canUseTTS       = promoFree || tier === 'tactical' || tier === 'ghost';
  const canUseDanger    = promoFree || tier === 'tactical' || tier === 'ghost';
  const canUseGameMode  = promoFree || tier === 'ghost';
  const canUseOwnKey    = tier === 'ghost'; // BYOK stays ghost-only — separate from feature access
  const radiusMiles     = promoFree ? 10 : tier === 'ghost' ? 10 : tier === 'tactical' ? 5 : tier === 'spotter' ? 2 : 1;
  const isFree          = tier === 'free';

  return {
    tier,
    subId,
    loading,
    activate,
    canUseAI,
    canUseTTS,
    canUseDanger,
    canUseGameMode,
    canUseOwnKey,
    radiusMiles,
    isFree,
  };
}
